import type { ReactNode } from 'react';
import { Box, HStack, Input, IconButton, Text, VStack } from '@chakra-ui/react';
import { Check } from 'lucide-react';

export interface InlineEditTableItem {
  id: string;
  label: string;
}

interface InlineEditTableProps<T extends InlineEditTableItem> {
  items: T[];
  renderItemActions?: (item: T, index: number) => ReactNode;
  renderItemLabel?: (item: T, index: number) => ReactNode;
  inputPrefix?: string;
  inputPlaceholder?: string;
  inputValue: string;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  canSubmit?: boolean;
  isDisabled?: boolean;
  error?: string | null;
  inputTestId?: string;
}

export function InlineEditTable<T extends InlineEditTableItem>({
  items,
  renderItemActions,
  renderItemLabel,
  inputPrefix,
  inputPlaceholder,
  inputValue,
  onInputChange,
  onSubmit,
  canSubmit = true,
  isDisabled,
  error,
  inputTestId,
}: InlineEditTableProps<T>) {
  return (
    <Box borderWidth="1px" borderColor="border.default" borderRadius="md" overflow="hidden">
      <VStack spacing={0} align="stretch">
        {items.map((item, index) => (
          <HStack
            key={item.id}
            spacing={3}
            px={3}
            py={2}
            minH="48px"
            borderBottomWidth="1px"
            borderColor="border.default"
          >
            <Text fontSize="sm" flex="1">
              {renderItemLabel != null
                ? renderItemLabel(item, index)
                : inputPrefix != null
                  ? `${inputPrefix}${item.label}`
                  : item.label}
            </Text>
            {renderItemActions?.(item, index)}
          </HStack>
        ))}
        <HStack spacing={2} px={3} py={2}>
          {inputPrefix != null ? (
            <Text fontSize="sm" color="fg.muted" flexShrink={0}>
              {inputPrefix}
            </Text>
          ) : null}
          <Input
            placeholder={inputPlaceholder}
            value={inputValue}
            size="sm"
            flex="1"
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSubmit();
              }
            }}
            isDisabled={isDisabled}
            data-testid={inputTestId}
          />
          {inputValue.trim().length > 0 && error == null && canSubmit ? (
            <IconButton
              aria-label="Save"
              icon={<Check size={16} />}
              size="xs"
              variant="ghost"
              onClick={onSubmit}
            />
          ) : null}
        </HStack>
        {error != null ? (
          <Text fontSize="sm" color="red.500" px={3} pb={2}>
            {error}
          </Text>
        ) : null}
      </VStack>
    </Box>
  );
}
